import { Box, Stack, Typography, useMediaQuery } from "@mui/material";
import React, { useEffect, useState } from "react";
import { colors } from "../../theme/theme";
import ImageSlider from "../../Components/Product/ImageSlider";
import CustomAccordion from "../../Components/Common/CustomAccordion";
import Footer from "../../Components/Common/Footer";
import { useLocation, useSearchParams } from "react-router-dom";
import SizeSelector from "../../Components/Product/SizeSelector";
import { logUserAction } from "../../utils/analytics";

const ProductPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Get search query from location.state first, then URL params, then empty string
  const searchQuery =
    location.state?.searchQuery || searchParams.get("q") || "";
  const product = location.state?.product || {};

  const [openIndexes, setOpenIndexes] = useState([0, 1]);

  const toggleAccordion = (index) => {
    setOpenIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const smallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "instant",
    });
  }, []);

  return (
    <Box sx={{ backgroundColor: "#FFFFFF", minHeight: "100vh" }}>
      <Stack
        direction={smallScreen ? "column" : "row"}
        sx={{
          maxWidth: "1400px",
          mx: "auto",
          alignItems: "stretch",
          pt: smallScreen ? 0 : 3,
          px: smallScreen ? 0 : 3,
          gap: smallScreen ? 0 : 3,
        }}
      >
        <ImageSlider
          productImages={
            product?.images?.length > 0
              ? product?.images
              : [product?.image_url] || []
          }
          searchQuery={searchQuery}
        />
        <Box
          sx={{
            p: smallScreen ? 3 : 4,
            width: smallScreen ? "100%" : "50%",
            backgroundColor: "#fff",
            position: "relative",
            borderRadius: smallScreen ? 0 : "12px",
            boxShadow: smallScreen ? "none" : "0 1px 3px rgba(0,0,0,0.1)",
            border: smallScreen ? "none" : "1px solid #eee",
          }}
        >
          {product?.price?.sale_percentage != null && (
            <Box
              sx={{
                position: "absolute",
                top: smallScreen ? "16px" : "24px",
                right: smallScreen ? "16px" : "24px",
                backgroundColor: "#e74c3c",
                color: "#fff",
                borderRadius: "4px",
                padding: "6px 10px",
                fontSize: "13px",
                fontFamily: "Poppins-SemiBold",
                fontWeight: 600,
                zIndex: 2,
              }}
            >
              -{Math.round(product?.price?.sale_percentage)}% OFF
            </Box>
          )}

          <Box>
            {/* Brand */}
            <Typography
              sx={{
                fontSize: "12px",
                fontFamily: "Poppins-Medium",
                fontWeight: 500,
                color: "#999",
                textTransform: "uppercase",
                mb: 1.5,
              }}
            >
              {product?.brand}
            </Typography>

            <Typography
              sx={{
                color: "#1a1a1a",
                fontFamily: "Poppins-Bold",
                fontWeight: 700,
                fontSize: smallScreen ? "24px" : "28px",
                lineHeight: 1.3,
                mb: 1,
              }}
            >
              {product?.title}
            </Typography>

            {/* Price Section */}
            <Box sx={{ mt: 2, mb: smallScreen ? 1.5 : 3 }}>
              <Stack direction="row" alignItems="baseline" spacing={1.5}>
                <Typography
                  sx={{
                    fontSize: smallScreen ? "28px" : "32px",
                    fontWeight: 800,
                    color: "#1a1a1a",
                    fontFamily: "Poppins-Bold",
                  }}
                >
                  {product?.price?.discounted || product?.price?.original}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: smallScreen ? "18px" : "20px",
                      fontWeight: 600,
                      color: "#666",
                      ml: 0.5,
                    }}
                  >
                    {product?.price?.currency}
                  </Typography>
                </Typography>
                {product?.price?.discounted && product?.price?.original !== product?.price?.discounted && (
                  <Typography
                    sx={{
                      fontSize: smallScreen ? "18px" : "20px",
                      color: "#999",
                      fontFamily: "Poppins-Regular",
                      textDecoration: "line-through",
                    }}
                  >
                    {product?.price?.original}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Box>

          {/* Color Selection */}
          {/* <ColorSelector /> */}

          {/* Size Selector */}
          {product?.variants?.length > 0 && (
            <Box sx={{ mt: smallScreen ? 2 : 3, mb: smallScreen ? 3 : 4 }}>
              <Typography
                sx={{
                  fontFamily: "Poppins-SemiBold",
                  fontWeight: 600,
                  color: "#1a1a1a",
                  fontSize: "13px",
                  mb: 1.5,
                }}
              >
                Available Sizes
              </Typography>
              <SizeSelector sizes={product?.variants} />
            </Box>
          )}

          {/* CTA Button */}
          <Box
            sx={{
              width: "100%",
              background: "linear-gradient(135deg, #FFB79C 0%, #FFA785 100%)",
              my: 3,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "8px",
              py: 2,
              cursor: "pointer",
              transition: "opacity 0.2s ease",
              "&:hover": {
                opacity: 0.9,
              },
            }}
            onClick={() => {
              if (product?.url) {
                window.open(product.url, "_blank");
              }

              // Run logging without blocking popup
              logUserAction({
                action: "checkout_redirect",
                data: {
                  product_id: product?.product_id || "",
                  product_title: product?.title || "",
                  brand: product?.brand || "",
                  redirect_url: product?.url || null,
                },
                timestamp: new Date().toISOString(),
              });
            }}
          >
            <Typography
              sx={{
                fontFamily: "Poppins-SemiBold",
                fontWeight: 600,
                color: "#fff",
                fontSize: "15px",
              }}
            >
              Buy on {product?.brand}
            </Typography>
          </Box>

          {/* Product Details Section */}
          <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #f0f0f0" }}>
            <CustomAccordion
              title="Product Description"
              content={product?.description}
              isOpen={openIndexes.includes(0)}
              toggle={() => toggleAccordion(0)}
            />
          </Box>
        </Box>
      </Stack>
      <Footer />
    </Box>
  );
};

export default ProductPage;
